import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeWallpaper, DEFAULT_WALLPAPER } from '../calculators/wallpaper'
import type { WallpaperInputs } from '../calculators/wallpaper'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/wallpaper/'
const CATEGORY = 'paint-and-walls'

interface CalcContent {
  title: string
  desc: string
  h1: string
  intro: string
  roomDimensions: string
  doorsTip: string
  windowsTip: string
  doorsSuffix: string
  windowsSuffix: string
  rollSpec: string
  rollSpecTip: string
  rollWidth: string
  rollLength: string
  inchSuffix: string
  ftSuffix: string
  patternRepeat: string
  patternTip: string
  stripNeedsPrefix: string
  stripNeedsSuffix: string
  stripWord: (n: number) => string
  perRoll: string
  wasteTip: string
  netSqft: string
  stripsWord: string
  rollsWord: string
  stripsPerRollSuffix: string
  primaryName: string
  primaryLabel: string
  savePrefix: string
  aboutH2: string
  aboutP1: string
  howH2: string
  howP1: string
  formula: string
  howP2: string
  mistakesH3: string
  mistakes: string[]
  relatedH2: string
  reviewer: string
  faqs: QA[]
}

const EN: CalcContent = {
  title: 'Wallpaper Calculator — How Many Rolls Do I Need? | RenoSheets',
  desc: 'Free wallpaper calculator with pattern repeat support. Enter room size and roll spec for exact roll count plus paste, primer and tools. No signup.',
  h1: 'Wallpaper Calculator',
  intro: 'Enter room dimensions and roll spec for exact roll count — with pattern repeat support, paste and primer.',
  roomDimensions: 'Room dimensions',
  doorsTip: 'Deducts ~20 sq ft per door opening.',
  windowsTip: 'Deducts ~15 sq ft per window.',
  doorsSuffix: 'doors',
  windowsSuffix: 'windows',
  rollSpec: 'Roll specification',
  rollSpecTip: 'Check the label or product page. Euro standard is 20.5 in wide x 33 ft long. American double-roll bolts are often 27 in x 13.5 ft.',
  rollWidth: 'Roll width',
  rollLength: 'Roll length',
  inchSuffix: 'in',
  ftSuffix: 'ft',
  patternRepeat: 'Pattern repeat',
  patternTip: 'Distance in inches between repeating pattern motifs. Set to 0 for solid, texture or random-match wallpaper. Found on the label as \'Repeat\' or \'V-match\'.',
  stripNeedsPrefix: 'Each strip needs',
  stripNeedsSuffix: 'ft (ceiling + 1 repeat for matching).',
  stripWord: (n: number) => `strip${n === 1 ? '' : 's'}`,
  perRoll: 'per roll.',
  wasteTip: '15% is standard for plain or random patterns. Use 20–25% for large pattern repeats, bold geometrics, or complex rooms.',
  netSqft: 'sq ft net',
  stripsWord: 'strips',
  rollsWord: 'rolls',
  stripsPerRollSuffix: 'strips per roll',
  primaryName: 'Wallpaper rolls',
  primaryLabel: 'rolls needed',
  savePrefix: 'Wallpaper',
  aboutH2: 'What actually affects how much wallpaper you need',
  aboutP1: 'Wallpaper waste comes almost entirely from pattern matching, not simple math errors. A plain or textured paper with no repeat wastes very little — each strip cuts close to the ceiling height. A large-scale pattern, on the other hand, forces you to shift the cut point on every strip so the design lines up at the seams, and that offset can eat a foot or more per strip depending on the repeat size. Room height matters too: taller ceilings mean fewer strips per roll, since strips can\'t be pieced together mid-wall. Pre-pasted paper is faster to hang but doesn\'t change the quantity math — the roll coverage is the same either way. This calculator adjusts waste automatically for pattern repeat so a large floral print doesn\'t get treated the same as a plain grasscloth.',
  howH2: 'How to calculate wallpaper rolls',
  howP1: 'Wallpaper is calculated in strips, not square feet, because each strip must run full height:',
  formula: `strip length = ceiling height + pattern repeat (ft)
strips per roll = floor(roll length ft / strip length)
total strips = ceil(wall area with waste / (roll width ft × strip length))
rolls = ceil(total strips / strips per roll)`,
  howP2: 'A 12-in pattern repeat on 9 ft ceilings means each strip takes 10 ft from the roll, giving 3 strips per 33 ft roll instead of 3.67. Always round up rolls — you can return unopened rolls, but matching a run later is nearly impossible.',
  mistakesH3: 'Common mistakes',
  mistakes: [
    'Not priming new drywall — paste soaks in and the paper won\'t stick properly or will tear on removal.',
    'Ignoring the dye lot number — rolls from different lots vary in shade. Buy all rolls from one order.',
    'Overlapping seams instead of butting them — overlaps create raised ridges that show under light.',
    'Hanging without a plumb line — walls are never perfectly square; start every room with a level vertical line.',
  ],
  relatedH2: 'Related calculators',
  reviewer: 'Reviewed by the RenoSheets team for calculation accuracy. Method: strip-based calculation with pattern repeat deduction; rolls rounded up. Last updated 2026-05-24. Estimates only — verify quantities before purchasing.',
  faqs: [
    { q: 'How many rolls of wallpaper do I need for a 12x10 room?', a: 'A 12 × 10 ft room with 9 ft ceilings has about 396 sq ft of wall area. With 20.5 in wide Euro rolls (33 ft long) and no pattern repeat, you need about 8–9 rolls including 15% waste. The exact count depends on the roll spec and any deductions for doors and windows.' },
    { q: 'How do you calculate wallpaper with a pattern repeat?', a: 'Each strip must start at the same point in the pattern, so some wallpaper is wasted at the top of each strip. Add the full pattern repeat length to each strip\'s cutting length. For example, with 9 ft ceilings and a 12-in repeat, each strip needs 10 ft of wallpaper, reducing strips per roll from 3 to 3.' },
    { q: 'What is the standard roll size for wallpaper?', a: 'European rolls are typically 20.5 in (52 cm) wide and 33 ft (10 m) long — this is the most common size in Canada and the US. Some specialty papers are 27 in or 54 in wide and come in 15 ft bolts. Always check the roll spec on the label before buying.' },
    { q: 'How much wallpaper paste do I need?', a: 'Plan on one bucket of wallpaper paste for every 4 rolls. The type of paste matters: use standard adhesive for most papers, heavy-duty for heavyweight or embossed papers, and starch paste for natural fibres. Always follow the manufacturer\'s recommendation on the wallpaper label.' },
    { q: 'Do I need to prime the wall before hanging wallpaper?', a: 'Yes — always apply a wallpaper primer or sizing coat to new drywall before hanging. Primer seals the porous surface so the paste grabs evenly and, crucially, makes removal much easier in the future. Skip primer on walls that have been previously wallpapered and are in good condition.' },
    { q: 'How much extra wallpaper should I buy for waste?', a: 'The standard waste allowance is 15% for a random or no-repeat design. Increase to 20% for patterns with a repeat under 6 in, and 25% for large bold patterns or rooms with many obstacles. Extra waste covers cuts, mismatches, and one or two strips for future touch-ups.' },
  ],
}

const FR: CalcContent = {
  title: 'Calculateur de papier peint — Combien de rouleaux me faut-il? | RenoSheets',
  desc: 'Calculateur de papier peint gratuit avec prise en charge du raccord de motif. Entrez la taille de la pièce et les spécifications du rouleau pour le nombre exact de rouleaux, plus la colle, l’apprêt et les outils. Sans inscription.',
  h1: 'Calculateur de papier peint',
  intro: 'Entrez les dimensions de la pièce et les spécifications du rouleau pour le nombre exact de rouleaux — avec prise en charge du raccord de motif, de la colle et de l’apprêt.',
  roomDimensions: 'Dimensions de la pièce',
  doorsTip: 'Déduit ~20 pi² par ouverture de porte.',
  windowsTip: 'Déduit ~15 pi² par fenêtre.',
  doorsSuffix: 'portes',
  windowsSuffix: 'fenêtres',
  rollSpec: 'Spécifications du rouleau',
  rollSpecTip: 'Consultez l’étiquette ou la fiche produit. Le standard européen est de 20,5 po de large x 33 pi de long. Les rouleaux doubles américains font souvent 27 po x 13,5 pi.',
  rollWidth: 'Largeur du rouleau',
  rollLength: 'Longueur du rouleau',
  inchSuffix: 'po',
  ftSuffix: 'pi',
  patternRepeat: 'Raccord de motif',
  patternTip: 'Distance en pouces entre les motifs qui se répètent. Mettez 0 pour un papier peint uni, texturé ou à raccord aléatoire. Indiqué sur l’étiquette comme « Raccord » ou « Raccord sauté ».',
  stripNeedsPrefix: 'Chaque lé nécessite',
  stripNeedsSuffix: 'pi (hauteur + 1 raccord pour l’ajustement).',
  stripWord: (n: number) => (n === 1 ? 'lé' : 'lés'),
  perRoll: 'par rouleau.',
  wasteTip: '15 % est la norme pour les motifs unis ou aléatoires. Utilisez 20–25 % pour les grands raccords de motif, les motifs géométriques marqués ou les pièces complexes.',
  netSqft: 'pi² net',
  stripsWord: 'lés',
  rollsWord: 'rouleaux',
  stripsPerRollSuffix: 'lés par rouleau',
  primaryName: 'Rouleaux de papier peint',
  primaryLabel: 'rouleaux nécessaires',
  savePrefix: 'Papier peint',
  aboutH2: 'Ce qui influence vraiment la quantité de papier peint nécessaire',
  aboutP1: 'La perte de papier peint vient presque entièrement de l’alignement du motif, pas d’erreurs de calcul. Un papier uni ou texturé sans répétition gaspille très peu — chaque lé se coupe près de la hauteur du plafond. Un motif à grande échelle, en revanche, oblige à décaler le point de coupe à chaque lé pour que le motif s’aligne aux joints, et ce décalage peut faire perdre un pied ou plus par lé selon la taille de la répétition. La hauteur de la pièce compte aussi : des plafonds plus hauts donnent moins de lés par rouleau, puisqu’un lé ne peut pas être assemblé en plein mur. Le papier pré-encollé se pose plus vite mais ne change rien au calcul de quantité — la couverture du rouleau reste la même. Ce calculateur ajuste automatiquement la perte selon la répétition du motif pour qu’un grand imprimé floral ne soit pas traité comme un papier de lin uni.',
  howH2: 'Comment calculer le nombre de rouleaux de papier peint',
  howP1: 'Le papier peint se calcule en lés, pas en pieds carrés, car chaque lé doit couvrir toute la hauteur :',
  formula: `longueur du lé = hauteur du plafond + raccord de motif (pi)
lés par rouleau = arrondi inf.(longueur du rouleau pi / longueur du lé)
total des lés = arrondi sup.(surface murale avec perte / (largeur du rouleau pi × longueur du lé))
rouleaux = arrondi sup.(total des lés / lés par rouleau)`,
  howP2: 'Un raccord de motif de 12 po sur des plafonds de 9 pi signifie que chaque lé prend 10 pi du rouleau, donnant 3 lés par rouleau de 33 pi au lieu de 3,67. Arrondissez toujours les rouleaux vers le haut — vous pouvez retourner les rouleaux non ouverts, mais assortir un nouveau lot plus tard est presque impossible.',
  mistakesH3: 'Erreurs fréquentes',
  mistakes: [
    'Ne pas apprêter le gypse neuf — la colle pénètre et le papier n’adhère pas bien ou se déchire au décollage.',
    'Ignorer le numéro de lot de teinture — les rouleaux de lots différents varient en teinte. Achetez tous les rouleaux d’une même commande.',
    'Chevaucher les joints au lieu de les abouter — les chevauchements créent des bourrelets visibles sous la lumière.',
    'Poser sans fil à plomb — les murs ne sont jamais parfaitement d’équerre; commencez chaque pièce par une ligne verticale de niveau.',
  ],
  relatedH2: 'Calculateurs connexes',
  reviewer: 'Révisé par l’équipe RenoSheets pour l’exactitude des calculs. Méthode : calcul par lés avec déduction du raccord de motif; rouleaux arrondis vers le haut. Dernière mise à jour 2026-05-24. Estimations seulement — vérifiez les quantités avant d’acheter.',
  faqs: [
    { q: 'Combien de rouleaux de papier peint me faut-il pour une pièce de 12 x 10?', a: 'Une pièce de 12 × 10 pi avec des plafonds de 9 pi a environ 396 pi² de surface murale. Avec des rouleaux européens de 20,5 po de large (33 pi de long) et sans raccord de motif, il vous faut environ 8 à 9 rouleaux incluant 15 % de perte. Le nombre exact dépend des spécifications du rouleau et des déductions pour les portes et fenêtres.' },
    { q: 'Comment calcule-t-on le papier peint avec un raccord de motif?', a: 'Chaque lé doit commencer au même point du motif, alors une partie du papier peint est perdue en haut de chaque lé. Ajoutez la longueur complète du raccord de motif à la longueur de coupe de chaque lé. Par exemple, avec des plafonds de 9 pi et un raccord de 12 po, chaque lé nécessite 10 pi de papier peint, réduisant le nombre de lés par rouleau de 3 à 3.' },
    { q: 'Quelle est la taille standard d’un rouleau de papier peint?', a: 'Les rouleaux européens font habituellement 20,5 po (52 cm) de large et 33 pi (10 m) de long — c’est la taille la plus courante au Canada et aux États-Unis. Certains papiers spécialisés font 27 po ou 54 po de large et viennent en rouleaux de 15 pi. Vérifiez toujours les spécifications du rouleau sur l’étiquette avant d’acheter.' },
    { q: 'Combien de colle à papier peint me faut-il?', a: 'Prévoyez un seau de colle à papier peint par 4 rouleaux. Le type de colle compte : utilisez une colle standard pour la plupart des papiers, une colle robuste pour les papiers lourds ou gaufrés, et une colle d’amidon pour les fibres naturelles. Suivez toujours la recommandation du fabricant sur l’étiquette du papier peint.' },
    { q: 'Dois-je apprêter le mur avant de poser le papier peint?', a: 'Oui — appliquez toujours un apprêt ou un fixateur à papier peint sur le gypse neuf avant la pose. L’apprêt scelle la surface poreuse pour que la colle adhère uniformément et, surtout, facilite grandement le décollage à l’avenir. Sautez l’apprêt sur les murs déjà tapissés et en bon état.' },
    { q: 'Combien de papier peint en plus dois-je acheter pour la perte?', a: 'La marge de perte standard est de 15 % pour un motif aléatoire ou sans raccord. Augmentez à 20 % pour les motifs avec un raccord de moins de 6 po, et à 25 % pour les grands motifs marqués ou les pièces avec beaucoup d’obstacles. La perte additionnelle couvre les coupes, les écarts d’assortiment et un ou deux lés pour des retouches futures.' },
  ],
}

export default function WallpaperCalculator() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<WallpaperInputs>(DEFAULT_WALLPAPER)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeWallpaper(i, locale), [i, locale])
  const set = (patch: Partial<WallpaperInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }
  const lin = i.unit === 'imperial' ? (locale === 'fr' ? 'pi' : 'ft') : 'm'

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'wallpaper',
      name: `${c.savePrefix} — ${r.wallArea} ${c.netSqft}`,
      createdAt: new Date().toISOString(),
      summary: `${r.rolls} ${c.rollsWord} · ${r.strips} ${c.stripsWord} · ${r.pasteBuckets} ${locale === 'fr' ? 'seaux de colle' : 'buckets paste'}`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'wallpaper')

  const assumptions = locale === 'fr'
    ? `${r.wallArea} pi² net · ${r.strips} lés · ${r.stripsPerRoll} lés par rouleau · ${i.wastePct} % de perte.`
    : `${r.wallArea} sq ft net · ${r.strips} strips · ${r.stripsPerRoll} strips per roll · ${i.wastePct}% waste.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeWallpaper: wall area − openings, × (1 + waste%), then strips
  // and rolls based on roll spec + pattern repeat.
  const sqftWord = locale === 'fr' ? 'pi²' : 'sq ft'
  const buildExplain = (): React.ReactNode => {
    const stripLengthFt = i.ceilingHeight + Math.max(0, i.patternRepeatIn) / 12
    const rollWidthFt = Math.max(0.5, i.rollWidthIn) / 12
    return (
      <ol className="list-decimal space-y-1 pl-5">
        {i.inputMode === 'dimensions' ? (
          <>
            <li>
              {locale === 'fr' ? 'Surface brute des murs' : 'Gross wall area'}
              {` (2 × (${i.length}+${i.width}) × ${i.ceilingHeight})`}:
              {' '}<strong>{Math.round(2 * (i.length + i.width) * i.ceilingHeight)} {sqftWord}</strong>
            </li>
            <li>
              {locale === 'fr'
                ? `− Ouvertures (${i.doors} porte${i.doors === 1 ? '' : 's'} × 20 + ${i.windows} fenêtre${i.windows === 1 ? '' : 's'} × 15)`
                : `− Openings (${i.doors} door${i.doors === 1 ? '' : 's'} × 20 + ${i.windows} window${i.windows === 1 ? '' : 's'} × 15)`}:
              {' '}<strong>−{i.doors * 20 + i.windows * 15} {sqftWord}</strong>
            </li>
            <li>= {locale === 'fr' ? 'Surface murale nette' : 'Net wall area'}: <strong>{r.wallArea} {sqftWord}</strong></li>
          </>
        ) : (
          <li>{locale === 'fr' ? 'Surface murale saisie' : 'Wall area you entered'}: <strong>{r.wallArea} {sqftWord}</strong></li>
        )}
        <li>{locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}: <strong>{r.wallAreaWithWaste} {sqftWord}</strong></li>
        <li>
          {locale === 'fr'
            ? `Longueur d’un lé = ${i.ceilingHeight} + ${(i.patternRepeatIn / 12).toFixed(2)} (raccord)`
            : `Strip length = ${i.ceilingHeight} + ${(i.patternRepeatIn / 12).toFixed(2)} (repeat)`}
          : <strong>{stripLengthFt.toFixed(2)} {locale === 'fr' ? 'pi' : 'ft'}</strong>
        </li>
        <li>
          {locale === 'fr'
            ? `Surface d’un lé = ${rollWidthFt.toFixed(2)} × ${stripLengthFt.toFixed(2)}`
            : `Strip area = ${rollWidthFt.toFixed(2)} × ${stripLengthFt.toFixed(2)}`}
          : <strong>{(rollWidthFt * stripLengthFt).toFixed(2)} {sqftWord}</strong>
        </li>
        <li>
          {locale === 'fr' ? '÷ surface d’un lé' : '÷ strip area'} = <strong>{r.strips} {locale === 'fr' ? (r.strips === 1 ? 'lé' : 'lés') : (r.strips === 1 ? 'strip' : 'strips')}</strong>
        </li>
        <li>
          {locale === 'fr'
            ? `÷ ${r.stripsPerRoll} lés par rouleau (${i.rollLengthFt} pi ÷ ${stripLengthFt.toFixed(2)} pi)`
            : `÷ ${r.stripsPerRoll} strips per roll (${i.rollLengthFt} ft ÷ ${stripLengthFt.toFixed(2)} ft)`}
          = <strong>{r.rolls} {locale === 'fr' ? (r.rolls === 1 ? 'rouleau' : 'rouleaux') : (r.rolls === 1 ? 'roll' : 'rolls')}</strong>
        </li>
      </ol>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={c.title} description={c.desc} path={PATH} schema={softwareAppSchema(calcName('wallpaper', 'Wallpaper Calculator', locale), c.desc, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Paint & Walls', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('wallpaper', 'Wallpaper Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-1 max-w-content text-text-muted">{c.intro}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-5 flex flex-wrap gap-6">
            <Segmented
              label={tr('calc.input')}
              value={i.inputMode}
              onChange={(v) => {
                if (v === 'area' && i.inputMode === 'dimensions') {
                  const inUserUnit = i.unit === 'metric' ? r.wallArea * 0.09290304 : r.wallArea
                  set({ inputMode: v, totalArea: Math.round(inUserUnit * 10) / 10 })
                } else {
                  set({ inputMode: v })
                }
              }}
              options={[{ v: 'dimensions', l: tr('calc.dimensions') }, { v: 'area', l: tr('calc.totalArea') }]}
            />
          </div>

          {/* Room — ceiling height ALWAYS visible (needed for strip length). */}
          <div className="rounded-md border border-border p-3">
            {i.inputMode === 'dimensions' ? (
              <>
                <p className="mb-2 text-sm font-medium text-text-body">{c.roomDimensions}</p>
                <div className="grid grid-cols-3 gap-2">
                  <SmartField label={tr('calc.length')} imperialValue={i.length} onChangeImperial={(n) => set({ length: n })} imperialUnit="ft" />
                  <SmartField label={tr('calc.width')} imperialValue={i.width} onChangeImperial={(n) => set({ width: n })} imperialUnit="ft" />
                  <SmartField label={tr('calc.ceilingHeight')} imperialValue={i.ceilingHeight} onChangeImperial={(n) => set({ ceilingHeight: n })} imperialUnit="ft" />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <span className="mb-1 flex items-center text-xs font-medium text-text-body">
                      {tr('calc.doors')}
                      <InfoTip text={c.doorsTip} />
                    </span>
                    <Field label="" value={i.doors} onChange={(n) => set({ doors: Math.round(n) })} suffix={c.doorsSuffix} />
                  </div>
                  <div>
                    <span className="mb-1 flex items-center text-xs font-medium text-text-body">
                      {tr('calc.windows')}
                      <InfoTip text={c.windowsTip} />
                    </span>
                    <Field label="" value={i.windows} onChange={(n) => set({ windows: Math.round(n) })} suffix={c.windowsSuffix} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <SmartField
                    label={tr('calc.totalArea')}
                    imperialValue={i.totalArea}
                    onChangeImperial={(n) => set({ totalArea: n })}
                    imperialUnit="sq ft"
                  />
                  <SmartField label={tr('calc.ceilingHeight')} imperialValue={i.ceilingHeight} onChangeImperial={(n) => set({ ceilingHeight: n })} imperialUnit="ft" />
                </div>
                <p className="mt-2 text-xs text-text-subtle">{tr('calc.areaHint')}</p>
              </>
            )}
          </div>

          {/* Roll spec */}
          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-text-body">
              {c.rollSpec}
              <InfoTip text={c.rollSpecTip} />
            </p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label={c.rollWidth} imperialValue={i.rollWidthIn} onChangeImperial={(n) => set({ rollWidthIn: n })} imperialUnit="in" />
              <SmartField label={c.rollLength} imperialValue={i.rollLengthFt} onChangeImperial={(n) => set({ rollLengthFt: n })} imperialUnit="ft" />
            </div>
          </div>

          {/* Pattern repeat */}
          <div className="mt-4">
            <span className="mb-1 flex items-center text-sm font-medium text-text-body">
              {c.patternRepeat}
              <InfoTip text={c.patternTip} />
            </span>
            <SmartField label="" imperialValue={i.patternRepeatIn} onChangeImperial={(n) => set({ patternRepeatIn: n })} imperialUnit="in" />
            {i.patternRepeatIn > 0 && (
              <p className="mt-1 text-xs text-text-subtle">
                {c.stripNeedsPrefix} {(i.ceilingHeight + i.patternRepeatIn / 12).toFixed(1)} {c.stripNeedsSuffix} {r.stripsPerRoll} {c.stripWord(r.stripsPerRoll)} {c.perRoll}
              </p>
            )}
          </div>

          {/* Waste */}
          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text={c.wasteTip} />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input
              type="range" min={10} max={25} step={1} value={i.wastePct}
              onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
              className="mt-1 w-full accent-brand"
            />
            <p className="mt-2 text-xs text-text-subtle">
              {r.wallArea} {c.netSqft} &rarr; {r.strips} {c.stripsWord} &rarr; {r.rolls} {c.rollsWord} ({r.stripsPerRoll} {c.stripsPerRollSuffix})
            </p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: c.primaryName, label: c.primaryLabel, qty: r.rolls, unit: 'roll', query: 'wallpaper rolls peel stick' }}
            secondary={null}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{c.aboutH2}</h2>
        <p className="mt-3">{c.aboutP1}</p>
        <h2 className="mt-8 text-2xl">{c.howH2}</h2>
        <p className="mt-3">{c.howP1}</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{c.formula}</pre>
        <p className="mt-3">{c.howP2}</p>
        <h3 className="mt-6 text-xl">{c.mistakesH3}</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {c.mistakes.map((m, k) => <li key={k}>{m}</li>)}
        </ul>
      </article>

      <Faq items={c.faqs} />

      <section className="mt-10">
        <h2 className="text-2xl">{c.relatedH2}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">{c.reviewer}</div>
    </div>
  )
}
